import { BehaviorSubject } from "rxjs";
let ws = null;
export let seriesComponentMapping = {};
let onCloseListener, onErrorListener;
import store from "@/store/index";
import Vue from "vue";

import { DownloadManager } from "dv-download-manager";
export let dm = new DownloadManager("alternate", false);

// Track per-series progress locally because Larvitar does not expose
// incremental download progress while streaming single images.
const downloadProgressState = {};

const clampProgress = value => Math.max(0, Math.min(100, value));

// Keep a lightweight progress mirror in the Vuex store for the UI.
const setDownloadProgress = (seriesId, value) => {
  store.commit("viewer/setDownloadProgress", {
    seriesId,
    value
  });
};

// Initialize progress and support resumed downloads (already-loaded images).
const initDownloadProgress = (seriesId, total, loaded = 0) => {
  if (!Number.isFinite(total) || total <= 0) {
    downloadProgressState[seriesId] = { total: 0, loaded: 0 };
    setDownloadProgress(seriesId, 0);
    return;
  }

  const safeLoaded = Math.min(Math.max(0, loaded), total);
  downloadProgressState[seriesId] = { total, loaded: safeLoaded };
  const progress = clampProgress(Math.round((100 * safeLoaded) / total));
  setDownloadProgress(seriesId, progress);
};

const incrementDownloadProgress = seriesId => {
  const entry = downloadProgressState[seriesId];
  if (!entry || !Number.isFinite(entry.total) || entry.total <= 0) {
    return;
  }

  // One message == one image parsed, update progress accordingly.
  entry.loaded = Math.min(entry.loaded + 1, entry.total);
  const progress = clampProgress(
    Math.round((100 * entry.loaded) / entry.total)
  );
  setDownloadProgress(seriesId, progress);
};

const finalizeDownloadProgress = seriesId => {
  setDownloadProgress(seriesId, 100);
  delete downloadProgressState[seriesId];
};

const clearDownloadProgress = seriesId => {
  delete downloadProgressState[seriesId];
  setDownloadProgress(seriesId, null);
};

import { decode } from "@msgpack/msgpack/dist";
const pako = require("pako");
const URL =
  window.location.protocol == "https:"
    ? `wss:/${window.location.host}/ws/series/download/`
    : `ws:/${window.location.host}/ws/series/download/`;

const wsStatus = new BehaviorSubject();

export const statusSource = wsStatus;

export const downloadDicomFiles = async (
  seriesId,
  elementId,
  images,
  updateProgress,
  options = {}
) => {
  seriesComponentMapping[seriesId] = updateProgress;

  // totalImages and alreadyLoaded allow a correct percentage even when
  // we resume a partially cached series.
  const totalImages = Number.isInteger(options.totalImages)
    ? options.totalImages
    : images.length;
  const alreadyLoaded = Number.isInteger(options.alreadyLoaded)
    ? options.alreadyLoaded
    : 0;

  initDownloadProgress(seriesId, totalImages, alreadyLoaded);

  // Flag this series for HTTP streaming if requested (for large images like MG/Tomo).
  if (options.useHTTP) {
    httpDownloadSeries.add(seriesId);
  }

  // add series to the download manager
  dm.addSeries(seriesId, seriesId, elementId, images);

  // start downloading if the download is not already in progress
  processDownloadQueue();
};

export const stopDownloadingDicomFiles = id => {
  dm.removeSeries(id);
  clearDownloadProgress(id);
  httpDownloadSeries.delete(id);
};

// Sliding window: keep up to WINDOW_SIZE requests in flight simultaneously.
// This overlaps network latency with client-side parsing for much faster downloads.
const DOWNLOAD_WINDOW_SIZE = 3;
let inFlight = 0;

// Series flagged for HTTP streaming (large images like MG, Tomo).
// When useHTTP option is passed to downloadDicomFiles, images are fetched via
// HTTP with ReadableStream for per-byte progress instead of WebSocket.
const httpDownloadSeries = new Set();

// Per-image download progress (only for HTTP-streamed large images).
const setImageDownloadProgress = (seriesId, imageId, percent) => {
  store.commit("viewer/setImageDownloadProgress", {
    seriesId,
    imageId,
    value: percent
  });
};

// Advance the sliding window: decrement in-flight counter, check series
// completion, and request the next image(s) from the queue.
const advanceQueue = seriesId => {
  inFlight = Math.max(0, inFlight - 1);
  incrementDownloadProgress(seriesId);
  const progress = dm.getStatus(seriesId);

  if (progress === null) {
    // download completed for the current series
    console.warn("Download ends for series", seriesId);
    store.commit("viewer/downloadEnded", { seriesId });
    finalizeDownloadProgress(seriesId);
    store.dispatch("raiseSnackbar", {
      text: `Download ends for series ${seriesId}: advanced views are now available`,
      color: "primary",
      timeout: 3000
    });
    dm.removeSeries(seriesId);
    httpDownloadSeries.delete(seriesId);
  }
  // Always try to fill the window — there may be other series queued
  processDownloadQueue();
};

// Download a single large image via HTTP with per-byte progress tracking.
// Unlike WebSocket (which only fires onmessage when the full frame arrives),
// fetch + ReadableStream exposes chunks as they come in, letting us show
// a smooth progress bar for large files (MG ~50-100 MB).
// Metadata (image_id, annotations) is passed via response headers to avoid
// a second round-trip; the body is pure DICOM bytes.
async function downloadImageHTTP(imageId, seriesId, elementId) {
  const response = await fetch(`/api/download-image/${imageId}/`, {
    credentials: "same-origin"
  });
  if (!response.ok) {
    throw new Error(`HTTP download failed: ${response.status}`);
  }

  const totalBytes = +response.headers.get("Content-Length") || 0;
  const imageUid = response.headers.get("X-Image-Id");
  const dbImageId = +response.headers.get("X-Db-Image-Id");
  const annotations = JSON.parse(
    response.headers.get("X-Annotations") || "[]"
  );

  // Read the response body as a stream, accumulating chunks and updating
  // per-image progress in the Vuex store for the UI to consume.
  const reader = response.body.getReader();
  let receivedBytes = 0;
  const chunks = [];
  let done = false;

  while (!done) {
    const result = await reader.read();
    done = result.done;

    if (result.value) {
      chunks.push(result.value);
      receivedBytes += result.value.length;

      if (totalBytes > 0) {
        const percent = Math.round((receivedBytes / totalBytes) * 100);
        setImageDownloadProgress(seriesId, imageId, percent);
      }
    }
  }

  if (annotations.length > 0) {
    store.commit("viewer/setAnnotations", {
      imageId: dbImageId,
      annotations
    });
  }

  // Reassemble the streamed chunks into a File for Larvitar parsing.
  const blob = new Blob(chunks);
  const file = new File([blob], imageUid);
  return { file, seriesId, elementId };
}

// interface with download manager
export function processDownloadQueue() {
  while (inFlight < DOWNLOAD_WINDOW_SIZE) {
    let slot = dm
      .getNextSlot(1)
      .slice()
      .pop();

    if (!slot) {
      break;
    }

    inFlight++;

    // Route through HTTP for per-byte progress when series is flagged, else WS.
    if (httpDownloadSeries.has(slot.seriesId)) {
      downloadImageHTTP(slot.imageId, slot.seriesId, slot.studyId)
        .then(({ file, seriesId, elementId }) => {
          return Vue.prototype.$dicom
            .parseFile(seriesId, file, elementId)
            .then(({ imageIds, imageId }) => {
              seriesComponentMapping[seriesId](
                seriesId,
                elementId,
                imageIds,
                imageId
              );
              advanceQueue(seriesId);
            });
        })
        .catch(error => {
          console.error(error);
          store.dispatch("raiseSnackbar", {
            text: error.message || "Failed to download image",
            color: "error",
            timeout: -1
          });
          advanceQueue(slot.seriesId);
        });
    } else {
      // Standard WebSocket path
      const wsMessage = {
        data: {
          images: [slot.imageId],
          elementIds: [slot.studyId]
        }
      };
      send(JSON.stringify(wsMessage));
    }
  }
}

export function openSeriesWebsocket(userID, onWsClose, onWsError, router) {
  if (ws) return;
  const onClose = event => {
    console.warn("WS", event.type, event);
    wsStatus.next(ws.readyState);
    onWsClose(event);
  };

  const onError = event => {
    console.warn("WS", event.type, event);
    if (ws.readyState == WebSocket.OPEN) {
      onWsError(event);
    }
  };

  ws = new WebSocket(URL + userID + `/`);
  wsStatus.next(ws.readyState);

  ws.onopen = () => {
    console.warn("Connected to WebSocket");
    wsStatus.next(ws.readyState);
  };

  onCloseListener = event => onClose(event);
  onErrorListener = event => onError(event);

  ws.onclose = onCloseListener;
  ws.onerror = onErrorListener;

  ws.onmessage = event => {
    console.log(event.data);
  };
  const onReceiveData = async (message, error) => {
    // Even when moving away from viewer during the download, 'progress' is null
    // viewer/downloadEnded already called on Viewer.vue::beforeDestroy()
    if (
      router.history.current.name !== "viewer" &&
      router.history.current.name !== "shared"
    )
      return;

    let data;
    if (message.data instanceof Blob) {
      data = decode(await message.data.arrayBuffer()).data;
    } else {
      store.dispatch("raiseSnackbar", {
        text: error || "Failed to download series: no data received",
        color: "error",
        timeout: -1
      });
      return;
    }
    if (data[0].annotations.length > 0) {
      store.commit("viewer/setAnnotations", {
        imageId: data[0].id,
        annotations: data[0].annotations
      });
    }
    // Decompress if the server sent zlib-compressed data
    const blob = data[0].compressed
      ? pako.inflate(data[0].blob)
      : data[0].blob;
    const file = new File([blob], data[0].image_id);
    Vue.prototype.$dicom
      .parseFile(data[0].series_id, file, data[0].element_id)
      .then(({ imageIds, imageId }) => {
        seriesComponentMapping[data[0].series_id](
          data[0].series_id,
          data[0].element_id,
          imageIds,
          imageId
        );
        advanceQueue(data[0].series_id);
      })
      .catch(error => {
        console.error(error);
        store.dispatch("raiseSnackbar", {
          text: error || "Failed to download series",
          color: "error",
          timeout: -1
        });
        // Skip the failed image and continue downloading the rest
        advanceQueue(data[0].series_id);
      });
  };

  setOnMessage(onReceiveData);
  setOnError(onReceiveData);
}

export function setOnMessage(onmessage) {
  ws.onmessage = onmessage;
}
export function setOnError(onerror) {
  ws.onerror = onerror;
}

export const canSendMessage = () => {
  return ws !== null && typeof ws.send === "function";
};

export const send = message => ws.send(message);

export const disconnect = () => {
  if (ws && typeof ws.close === "function") {
    ws.close();
    ws = null;
  }
};
